import { CommonEvents } from "@common/services/common-events";
import type SpaceStore from "@common/stores/space-store";
import type { SpaceId } from "@common/types/space";
import type { GroupKey, Todo, TodoId, TodoIdMap } from "@common/types/todo";
import TodoGroupUtils from "@common/utils/todo-group-utils";
import TodoUtils from "@common/utils/todo-utils";
import type { AreaSelectStore } from "@web/src/app/components/area-selection/area-select-store";
import type { DroppableId } from "@web/src/app/use-cases/id-flavors";
import { selectableIdToTodoId, todoIdToDroppableId } from "@web/src/app/use-cases/id-flavors";
import { handleWebError } from "@web/src/app/utils/web-errors";
import { WebEvents } from "@web/src/app/web-events";
import type { DroppableData, DroppableProvider } from "@web/src/hooks/dnd/dnd-store";
import { droppableGroupIdToGroupKey } from "@web/src/hooks/dnd/dnd-store";
import type UIStore from "@web/src/store/ui-store";

export type AllDraggableData = TodoDraggableData;
export type TodoDraggableData = {
  kind: "todo";
  todoId: TodoId;
};

// Used internally, a union of all possible types
export type AllDroppableData = (SpaceDroppableData | TodoDroppableData | AddTodoButtonDroppableData) &
  Pick<DroppableData<AllDroppableKind>, "groupId" | "droppableId" | "canDrop">;

export type TodoDroppableData = {
  kind: "todo";
  todoId: TodoId;
};

export type AddTodoButtonDroppableData = {
  kind: "add_todo_button";
  groupKey: GroupKey;
};

export type SpaceDroppableData = {
  kind: "space";
  spaceId: SpaceId;
};

export type AllDraggableKind = "todo";
export type AllDroppableKind = "todo" | "add_todo_button" | "space";

export type AllDndTypeBundle = {
  TDroppableKinds: AllDroppableKind;
  TDraggableKinds: AllDraggableKind;
  TDraggableData: AllDraggableData;
  TDroppableData: AllDroppableData;
};

function getTodosToMove(draggableData: TodoDraggableData, deps: DropHandlerDeps) {
  const { spaceStore, areaSelectStore } = deps;
  const todoStore = spaceStore.currentTodoStore;
  const selectionTodoIds: TodoId[] = [...areaSelectStore.selection].map(selectableIdToTodoId);
  const allDragIds = getAllDraggedTodoIds(draggableData.todoId, selectionTodoIds, todoStore.todoIdMap);
  const todoIdSet = new Set(allDragIds);
  const todosToMove: Todo[] = [];
  for (const todoId of allDragIds) {
    const todo = todoStore.todoIdMap[todoId];
    if (!todo) {
      console.warn("tried to move todo with id: ", todoId, " but it does not exist");
      continue;
    }

    // Only move todos whose parent's are not part of the selection
    // Since if the parent is part, they will be moved anywas
    if (todo.parent && todoIdSet.has(todo.parent)) continue;

    todosToMove.push(todo);
  }
  return todosToMove;
}

type DropHandlerDeps = {
  spaceStore: SpaceStore;
  uiStore: UIStore;
  areaSelectStore: AreaSelectStore;
};

export function makeDropHandlers(deps: DropHandlerDeps): Record<AllDroppableKind, DroppableProvider<AllDndTypeBundle>> {
  const TodoGroupDropProvider: DroppableProvider<AllDndTypeBundle> = {
    kind: "add_todo_button",
    handleDrop({ sourceId, targetId, dndStore }) {
      const draggableData = dndStore.currentDraggableData;
      if (!draggableData) throw new Error(`No data found for draggable ${sourceId}`);
      if (draggableData.kind !== "todo") {
        console.error("Drop provider not able to handle draggable kind", draggableData.kind);
        return;
      }

      const droppableData = dndStore.droppableDataMap.get(targetId);

      if (!droppableData) {
        console.error(`No droppableData for droppableId ${targetId}`);
        return;
      }

      if (!("groupKey" in droppableData))
        throw new Error("TodoGroupDropProvider does not correctly include a groupKey property in droppableData");

      const todosToMove = getTodosToMove(draggableData, deps);
      const todoIds = todosToMove.map((t) => t.id);
      const { spaceStore } = deps;
      const todoStore = spaceStore.currentTodoStore;

      // Insert in empty group: targetId will be a groupKey if it is empty
      const delta = TodoUtils.moveTodosToGroup(
        todoStore.todoIdMap,
        todoIds,
        droppableData.groupKey,
        todoStore.displayMode,
        todoStore.curHashtagFilterFn,
      );
      todoStore.updateTodos(delta);

      todoIds.forEach((todoId) => {
        WebEvents.emit("WEB_ON_MOVED_TODO", todoId);
      });
    },
  };

  const SpaceDropProvider: DroppableProvider<AllDndTypeBundle> = {
    kind: "space",
    async handleDrop({ sourceId, targetId, dndStore }) {
      const { spaceStore, uiStore } = deps;
      const draggableData = dndStore.draggableDataMap.get(sourceId);
      if (!draggableData) throw new Error(`No data found for draggable ${sourceId}`);
      if (draggableData.kind !== "todo") {
        console.error("Drop provider not able to handle draggable kind", draggableData.kind);
        return;
      }

      const droppableData = dndStore.droppableDataMap.get(targetId);
      if (!droppableData) throw new Error(`No data found for droppable ${sourceId}`);
      if (droppableData.kind !== "space") {
        console.error("Drop provider not able to handle droppable kind", droppableData.kind);
        return;
      }

      const todosToMove = getTodosToMove(draggableData, deps);

      const targetSpace = spaceStore.spaces.find((s) => s.id === targetId);
      if (!targetSpace) {
        handleWebError(new Error(`Space not found. Please refresh and try again.`), {
          overrideTitle: "Cannot perform action",
        });
        return;
      }

      if (targetSpace.id === spaceStore.currentSpaceId) {
        CommonEvents.emit("PUBLISH_TOAST", {
          title: "Cannot move todos",
          content: "Todos already belong to this space",
          titleColor: "warning",
          duration: 5000,
        });
        return;
      }
      uiStore.setModalProps({
        title: "Move Todos",
        content: `Move ${todosToMove.length} todo${todosToMove.length > 1 ? "s" : ""} (and their subtasks) to "${
          targetSpace.name
        }" space?\n\nTHIS ACTION CANNOT BE UNDONE`,
        actions: [
          {
            intent: "CANCEL",
            text: "Cancel",
            onClick: () => {},
          },
          {
            intent: "PRIMARY",
            text: "Confirm",
            default: true,
            onClick: async () => {
              const todoIds = todosToMove.map((t) => t.id);
              await spaceStore.migrateTodos(todoIds, spaceStore.currentSpaceId, droppableData.spaceId);
            },
          },
        ],
      });
    },
  };

  const TodoDropProvider: DroppableProvider<AllDndTypeBundle> = {
    kind: "todo",
    handleDrop({ position, sourceId, targetId, groupId, dndStore }) {
      const { spaceStore } = deps;
      const todoStore = spaceStore.currentTodoStore;
      const todoIdMap = todoStore.todoIdMap;
      const groupKey = droppableGroupIdToGroupKey(groupId);

      const draggableData = dndStore.draggableDataMap.get(sourceId);
      if (!draggableData) throw new Error(`No data found for draggable ${sourceId}`);
      if (draggableData.kind !== "todo") {
        console.error("Drop provider not able to handle draggable kind", draggableData.kind);
        return;
      }

      const droppableData = dndStore.droppableDataMap.get(targetId);
      if (!droppableData) throw new Error(`No data found for droppable ${targetId}`);
      if (droppableData.kind !== "todo") {
        console.error("Drop provider not able to handle droppable kind", droppableData.kind);
        return;
      }

      const todosToMove = getTodosToMove(draggableData, deps);
      const draggedTodoIds = todosToMove.map((t) => t.id);
      const draggedTodoIdsSet = new Set(draggedTodoIds);

      /**
       * There is a discrepancy between the todoIdMap
       * used for targetting (identifying where to insert)
       *
       * Even while targeting, isForcedChild is a function of
       * the full state, whereas maxNegativeIndent is a function of altered state
       *
       * and the todoIdMap used to performance the mutation
       * (identifying which todos to move i.e. when filters are on, parent child date fixing)
       */
      const sourceTodo = todoIdMap[draggableData.todoId];
      const targetTodo = todoIdMap[droppableData.todoId];

      if (draggedTodoIdsSet.has(droppableData.todoId)) {
        // throw new Error("Dnd: cannot target self!!")
        console.error("Dnd: cannot target dragged todo!!");
        return;
      }

      if (!sourceTodo) return; // not this store maybe
      if (!targetTodo) throw new Error(`dnd target missing in space ${todoStore.spaceId}`);

      const visibleTodoList =
        TodoGroupUtils.todoListByGroup(todoIdMap, todoStore.displayMode, todoStore.curHashtagFilterFn)[groupKey] ?? [];
      const visibleTodoListWithoutDrag = visibleTodoList.filter((t) => !draggedTodoIdsSet.has(t.id));

      if (todosToMove.find((t) => t.tag === "urgent") && groupId === "backlog") {
        CommonEvents.emit("PUBLISH_TOAST", {
          title: "Urgent Todo in Backlog",
          content: 'Tasks marked "urgent" will be moved to Today on refresh',
          titleColor: "warning",
        });
      }

      // based on position, identify how we should insert the todo
      // BEFORE: insert before as sibling (insert before is always as sibling)
      // only for when it is first in droppable group
      if (position.y === -1) {
        const delta = TodoUtils.insertTodosBefore(
          todoIdMap,
          todosToMove,
          droppableData.todoId,
          groupKey,
          todoStore.displayMode,
          todoStore.curHashtagFilterFn,
        );
        todoStore.updateTodos(delta);
      }
      // AFTER
      else if (position.y === 1) {
        // if a todo has a visible child, it must be dropped as a child
        const childrenOfTodo = TodoUtils.visibleChildrenOfTodo(targetTodo.id, visibleTodoList).filter(
          (t) => !draggedTodoIdsSet.has(t.id),
        );
        const isForcedChild = childrenOfTodo.length && !targetTodo.collapsed;

        // AFTER AS SIBLING
        if (position.x === 0 && !isForcedChild) {
          const delta = TodoUtils.insertTodosAfter(
            todoIdMap,
            todosToMove,
            targetTodo.parent,
            droppableData.todoId,
            groupKey,
            todoStore.displayMode,
            todoStore.curHashtagFilterFn,
          );
          todoStore.updateTodos(delta);
        }
        // AFTER AS CHILD
        else if (position.x === 1 || isForcedChild) {
          const delta = TodoUtils.insertTodosAfter(
            todoIdMap,
            todosToMove,
            targetTodo.id,
            droppableData.todoId,
            groupKey,
            todoStore.displayMode,
            todoStore.curHashtagFilterFn,
          );
          todoStore.updateTodos(delta);
        }
        // AFTER AS (GRAND)PARENT'S SIBLING
        else {
          const negativeIndent = Math.max(-position.x, 0);

          let targetTodo = todoIdMap[droppableData.todoId];
          if (!targetTodo) throw new Error("Target todo not in view");
          if (!groupId) throw new Error("No group id provided");
          const maxNegativeIndent = getMaxNegativeIndent(
            targetId,
            groupId,
            visibleTodoListWithoutDrag.map((t) => ({
              id: todoIdToDroppableId(t.id, groupKey),
              indent: t.indent,
            })),
            draggedTodoIdsSet,
          );
          const finalNegativeIndent = Math.min(maxNegativeIndent, negativeIndent ?? 0);

          let recurseCount = finalNegativeIndent;
          let insertAfterTodo = targetTodo;
          while (recurseCount > 0 && todoIdMap[insertAfterTodo.parent ?? ""]) {
            recurseCount--;
            insertAfterTodo = todoIdMap[insertAfterTodo.parent ?? ""]!;
          }

          const delta = TodoUtils.insertTodosAfter(
            todoStore.todoIdMap,
            todosToMove,
            insertAfterTodo.parent,
            insertAfterTodo.id,
            groupKey,
            todoStore.displayMode,
            todoStore.curHashtagFilterFn,
          );
          todoStore.updateTodos(delta);
        }
      } else {
        throw new Error("Invalid droppable y position");
      }
    },
  };

  return {
    space: SpaceDropProvider,
    add_todo_button: TodoGroupDropProvider,
    todo: TodoDropProvider,
  };
}

/**
 * We split source and selection for memoization purposes
 */
export const getAllDraggedTodoIds = (sourceTodoId: TodoId, selection: TodoId[], todoIdMap: TodoIdMap) => {
  const rootTodoIds = new Set([...selection, sourceTodoId]);
  const parentChildMap = TodoUtils.parentChildMap(Object.values<Todo>(todoIdMap));

  const allTodoIds = new Set<TodoId>();
  for (let rootTodoId of rootTodoIds) {
    allTodoIds.add(rootTodoId);
    const deepChildren = TodoUtils.deepChildrenOfTodo(todoIdMap, rootTodoId, parentChildMap);
    for (let child of deepChildren) {
      // if (allTodoIds.has(child.id)) {
      //   // IMPORTANT: selection takes priority - the child will be de-parented
      //   continue;
      // }
      allTodoIds.add(child.id);
    }
  }
  return allTodoIds;
};

export function getMaxNegativeIndent(
  targetId: DroppableId,
  groupId: string,
  itemList: { id: DroppableId; indent: number }[],
  draggedIds: Set<string>,
) {
  let nextItem: { indent: number } | undefined;
  let targetTodoWithIndent = itemList
    /**
     * Took a long time to settle on this,
     * but trying to filter out dragged ids on the todoIdMap level
     * just broke too many relationships (hashtags filtering, parent child)
     * doing it while targeting is the easiest
     */
    .filter((item) => !draggedIds.has(item.id)) // filter out dragged items
    .find((item, i) => {
      if (item.id === targetId) {
        nextItem = itemList[i + 1];
        return true;
      }
      return false;
    });

  if (!targetTodoWithIndent) {
    console.error(`getMaxNegativeIndent: Item: ${targetId} was not found in group: ${groupId}`);
    return 0;
  }

  return !nextItem
    ? Infinity
    : nextItem.indent >= targetTodoWithIndent.indent
      ? 0
      : targetTodoWithIndent.indent - nextItem.indent;
}
